import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { AGENT_NAV } from "@/lib/agent-nav";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { toast } from "sonner";
import { assertWritable } from "@/lib/viewAs";
import {
  DndContext,
  useDraggable,
  useDroppable,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MoreVertical } from "lucide-react";
import { getSlaStatus } from "@/lib/lead-sla";
import { Link } from "@tanstack/react-router";

export const Route = createFileRoute("/agent/leads")({
  head: () => ({
    meta: [
      { title: "Pipeline — Currency Thoughts" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: LeadsPage,
});

type Stage = "new" | "contacted" | "qualified" | "booked" | "converted" | "lost";
const COLUMNS: { id: Stage; label: string }[] = [
  { id: "new", label: "New" },
  { id: "contacted", label: "Contacted" },
  { id: "qualified", label: "Qualified" },
  { id: "booked", label: "Consultation Booked" },
  { id: "converted", label: "Converted" },
  { id: "lost", label: "Lost" },
];

interface Lead {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  source: string | null;
  stage: Stage;
  notes: string | null;
  created_at: string;
  assigned_agent_id: string | null;
}

interface LeadRow extends Lead {
  updated_at?: string | null;
}

function LeadsPage() {
  return (
    <ProtectedRoute requiredRole="agent">
      <PortalShell title="Pipeline" nav={AGENT_NAV}>
        <Kanban />
      </PortalShell>
    </ProtectedRoute>
  );
}

function ageDays(iso: string) {
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return `${d}d`;
}

function Kanban() {
  const { user, role } = useAuth();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [selected, setSelected] = useState<Lead | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = useCallback(async () => {
    if (!user) return;
    const q = supabase.from("leads").select("*").order("created_at", { ascending: false });
    const { data } = role === "admin" || role === "super_admin"
      ? await q
      : await q.eq("assigned_agent_id", user.id);
    setLeads((data ?? []) as Lead[]);
  }, [user, role]);

  useEffect(() => { void load(); }, [load]);

  const onDragEnd = async (e: DragEndEvent) => {
    if (!assertWritable()) return;
    const leadId = String(e.active.id);
    const to = e.over?.id as Stage | undefined;
    if (!to) return;
    const lead = leads.find((l) => l.id === leadId);
    if (!lead || lead.stage === to) return;
    const from = lead.stage;
    setLeads((ls) => ls.map((l) => (l.id === leadId ? { ...l, stage: to } : l)));
    const { error } = await supabase.from("leads").update({ stage: to }).eq("id", leadId);
    if (error) {
      setLeads((ls) => ls.map((l) => (l.id === leadId ? { ...l, stage: from } : l)));
      toast.error("Could not update lead");
      return;
    }
    await supabase.from("activity_logs").insert({
      user_id: user?.id,
      action: "lead.status_changed",
      target_type: "lead",
      target_id: leadId,
      metadata: { from, to },
    });
    toast.success(`Moved to ${COLUMNS.find((c) => c.id === to)?.label}`);
  };

  return (
    <div className="max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-semibold text-navy-800" style={{ fontSize: "36px", letterSpacing: "-0.01em" }}>
            <em className="text-bronze-600 italic font-normal">Pipeline</em>
          </h2>
          <p className="mt-1 font-sans text-[13px] text-charcoal-500">
            Drag a card between columns to advance its stage.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/agent/protocol"
            className="font-sans text-[12px] font-semibold uppercase tracking-[0.18em] text-bronze-600 hover:text-navy-800 border-b border-bronze-600/40 pb-0.5"
          >
            Lead Protocol
          </Link>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <PrimaryButton>Add Lead</PrimaryButton>
          </DialogTrigger>
          <AddLeadDialog
            onCreated={() => { setAddOpen(false); void load(); }}
            agentId={user?.id ?? null}
          />
          </Dialog>
        </div>
      </div>

      <div className="mt-8 -mx-2 overflow-x-auto pb-4">
        <DndContext sensors={sensors} onDragEnd={onDragEnd}>
          <div className="flex gap-3 px-2 min-w-max">
            {COLUMNS.map((col) => {
              const items = leads.filter((l) => l.stage === col.id);
              return (
                <Column key={col.id} id={col.id} label={col.label} count={items.length}>
                  {items.map((lead) => (
                    <LeadCard key={lead.id} lead={lead} onOpen={() => setSelected(lead)} />
                  ))}
                </Column>
              );
            })}
          </div>
        </DndContext>
      </div>

      <LeadDialog
        lead={selected}
        onClose={() => setSelected(null)}
        onChanged={() => { setSelected(null); void load(); }}
      />
    </div>
  );
}

function Column({ id, label, count, children }: { id: Stage; label: string; count: number; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className={`w-[280px] shrink-0 flex flex-col rounded-[4px] overflow-hidden border ${isOver ? "border-bronze-600" : "border-bronze-600/20"}`}>
      <div className="bg-navy-900 text-cream-50 px-4 py-3 flex items-center justify-between">
        <span className="font-sans text-[12px] font-semibold uppercase tracking-[0.18em]">{label}</span>
        <span className="bg-bronze-600 text-navy-900 font-sans text-[11px] font-semibold px-2 py-0.5 rounded-full">{count}</span>
      </div>
      <div className="bg-cream-50/60 flex-1 p-2 space-y-2 min-h-[60vh] max-h-[70vh] overflow-y-auto">
        {children}
      </div>
    </div>
  );
}

function LeadCard({ lead, onOpen }: { lead: Lead; onOpen: () => void }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: lead.id });
  const style: React.CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.6 : 1,
  };
  const reference = (lead as LeadRow).updated_at ?? lead.created_at;
  const sla = getSlaStatus(lead.stage, reference);
  const slaTone =
    sla.tone === "overdue"
      ? "bg-danger/10 text-danger border-danger/30"
      : sla.tone === "due-soon"
        ? "bg-warning/10 text-warning border-warning/30"
        : "bg-success/10 text-success border-success/30";
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onOpen}
      className="bg-white rounded-[4px] p-3 shadow-soft border border-bronze-600/15 cursor-grab active:cursor-grabbing hover:shadow-bronze-sm transition-shadow"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="font-sans font-semibold text-[14px] text-navy-800 leading-tight">{lead.full_name}</p>
        <MoreVertical size={14} className="text-charcoal-500 shrink-0" />
      </div>
      {lead.source && (
        <p className="mt-1 font-sans text-[11px] text-charcoal-500">Source: {lead.source}</p>
      )}
      <div className="mt-2 flex items-center justify-between">
        <span className="px-2 py-0.5 bg-bronze-400/30 text-navy-800 font-sans font-semibold text-[10px] uppercase tracking-[0.1em] rounded-sm">
          {lead.email ? "Email" : lead.phone ? "Phone" : "Inbound"}
        </span>
        <span className="font-sans text-[11px] text-charcoal-500">{ageDays(lead.created_at)}</span>
      </div>
      {sla.tone !== "none" && (
        <div className="mt-2 flex items-center justify-between">
          <span
            className={`px-2 py-0.5 border rounded-sm font-sans font-semibold text-[10px] uppercase tracking-[0.1em] ${slaTone}`}
          >
            SLA · {sla.label}
          </span>
        </div>
      )}
    </div>
  );
}

function AddLeadDialog({ onCreated, agentId }: { onCreated: () => void; agentId: string | null }) {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", source: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!assertWritable()) return;
    if (!form.full_name.trim()) return toast.error("Name is required");
    setBusy(true);
    const { error } = await supabase.from("leads").insert({
      full_name: form.full_name.trim(),
      email: form.email || null,
      phone: form.phone || null,
      source: form.source || null,
      notes: form.notes || null,
      stage: "new",
      assigned_agent_id: agentId,
    });
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Lead added");
    onCreated();
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle className="font-display text-navy-800">Add a Lead</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div><Label>Full name</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
        </div>
        <div><Label>Source</Label><Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="Referral, Web, Event…" /></div>
        <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} /></div>
        <PrimaryButton onClick={() => void submit()} disabled={busy} className="w-full">
          {busy ? "Saving…" : "Create Lead"}
        </PrimaryButton>
      </div>
    </DialogContent>
  );
}

function LeadDialog({ lead, onClose, onChanged }: { lead: Lead | null; onClose: () => void; onChanged: () => void }) {
  const { user } = useAuth();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  if (!lead) return null;

  const convert = async () => {
    if (!assertWritable()) return;
    if (!confirm(`Convert ${lead.full_name} to a client?`)) return;
    setBusy(true);
    // Mark converted and self-assign the agent.
    await supabase
      .from("leads")
      .update({ stage: "converted", assigned_agent_id: user?.id ?? null })
      .eq("id", lead.id);

    // If the lead's email matches an existing auth profile, link a clients row
    // and assign this agent. Otherwise the welcome email still goes out so
    // they can sign up.
    let linkedClientId: string | null = null;
    if (lead.email && user?.id) {
      const { data: prof } = await supabase
        .from("profiles")
        .select("id")
        .ilike("email", lead.email)
        .maybeSingle();
      if (prof?.id) {
        const { data: existing } = await supabase
          .from("clients")
          .select("id")
          .eq("user_id", prof.id)
          .maybeSingle();
        if (existing?.id) {
          await supabase
            .from("clients")
            .update({ assigned_agent_id: user.id })
            .eq("id", existing.id);
          linkedClientId = existing.id;
        } else {
          const { data: created } = await supabase
            .from("clients")
            .insert({ user_id: prof.id, assigned_agent_id: user.id })
            .select("id")
            .maybeSingle();
          linkedClientId = created?.id ?? null;
        }
      }
    }

    // Fire the welcome email (transactional). Failures are non-blocking.
    let welcomeEmailId: string | null = null;
    let emailFailed = false;
    if (lead.email) {
      const firstName = lead.full_name.trim().split(/\s+/)[0] || "there";
      try {
        const { data: emailRes, error: emailErr } = await supabase.functions.invoke(
          "send-email",
          {
            body: {
              template_name: "welcome",
              to: lead.email,
              category: "transactional",
              props: {
                firstName,
                dashboardUrl: "https://www.currencythoughts.net/client",
              },
            },
          },
        );
        if (emailErr) emailFailed = true;
        else
          welcomeEmailId =
            (emailRes as { email_id?: string } | null)?.email_id ?? null;
      } catch {
        emailFailed = true;
      }
    }

    await supabase.from("activity_logs").insert({
      user_id: user?.id,
      action: "lead.converted",
      target_type: "lead",
      target_id: lead.id,
      metadata: {
        welcome_email_id: welcomeEmailId,
        linked_client_id: linkedClientId,
        email_sent: !emailFailed && !!lead.email,
      },
    });

    setBusy(false);
    if (emailFailed) toast.warning("Lead converted (welcome email failed)");
    else toast.success("Lead converted");
    onChanged();
  };

  const addNote = async () => {
    if (!assertWritable()) return;
    if (!note.trim()) return;
    const stamped = `${new Date().toLocaleString()} — ${note.trim()}`;
    const merged = lead.notes ? `${lead.notes}\n\n${stamped}` : stamped;
    await supabase.from("leads").update({ notes: merged }).eq("id", lead.id);
    setNote("");
    onChanged();
  };

  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display text-navy-800 text-[24px]">{lead.full_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-[13px] font-sans">
            <div><span className="text-charcoal-500">Email:</span> <span className="text-charcoal-900">{lead.email || "—"}</span></div>
            <div><span className="text-charcoal-500">Phone:</span> <span className="text-charcoal-900">{lead.phone || "—"}</span></div>
            <div><span className="text-charcoal-500">Source:</span> <span className="text-charcoal-900">{lead.source || "—"}</span></div>
            <div><span className="text-charcoal-500">Stage:</span> <span className="text-charcoal-900">{lead.stage}</span></div>
          </div>

          <div>
            <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600 mb-2">Notes</p>
            <div className="bg-cream-50 border border-bronze-600/20 rounded-[4px] p-3 max-h-40 overflow-y-auto whitespace-pre-wrap font-sans text-[12px] text-charcoal-900">
              {lead.notes || <span className="text-charcoal-500">No notes yet.</span>}
            </div>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add a note…" className="mt-3" />
            <button onClick={() => void addNote()} className="mt-2 font-sans text-[12px] text-bronze-600 hover:text-navy-800">
              + Add Note
            </button>
          </div>

          {lead.stage !== "converted" && (
            <PrimaryButton onClick={() => void convert()} disabled={busy} className="w-full">
              {busy ? "Converting…" : "Convert to Client"}
            </PrimaryButton>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}